<template>
  <Box>
    <BoxHead
      icon="fas fa-calendar"
      :link="link"
    >
      Meetings
    </BoxHead>

    <LoaderWithText
      v-if="isFetching"
      size="3_5"
    />

    <div
      v-else
      class="meetings-body"
    >
      <Stack>
        <Stack
          gap="2"
          class="tw-grow"
        >
          <Subtitle
            size="3_5"
            class="meetings-heading"
          >
            Upcoming Meetings
          </Subtitle>

          <Stack
            v-if="nextMeeting"
            class="followups"
            gap="2"
          >
            <Stack
              class="next-meeting"
              gap="2"
            >
              <Stack gap="1">
                <Subtitle>
                  {{ relativeDate(nextMeeting.meeting_date) }}
                </Subtitle>

                <Title
                  v-if="nextMeeting.title"
                  size="5"
                >
                  {{ nextMeeting.title }}
                </Title>
              </Stack>

              <DetailsList>
                <DetailPair
                  label="Date"
                  :value="formatShortDate(nextMeeting.meeting_date)"
                />
                <DetailPair
                  v-if="nextMeeting.meeting_type"
                  label="Meeting Type"
                  :value="prettifyMixedCase(nextMeeting.meeting_type)"
                />
              </DetailsList>
            </Stack>

            <Stack
              v-if="upcomingMeeting"
              class="upcoming-meeting"
              gap="1"
            >
              <Subtitle
                size="3"
              >
                {{ relativeDate(upcomingMeeting.meeting_date) }}
              </Subtitle>

              <Title
                v-if="upcomingMeeting.title"
                size="4"
              >
                {{ upcomingMeeting.title }}
              </Title>
            </Stack>
          </Stack>

          <Callout
            v-else
            status="info"
            size="3_5"
          >
            No upcoming meeting
          </Callout>
        </Stack>
      </Stack>

      <Stack gap="2">
        <Subtitle
          size="3_5"
          class="meetings-heading"
        >
          Completed Meetings
        </Subtitle>

        <Stack
          v-if="topMeetings.length"
          gap="2"
        >
          <Stack
            gap="0"
            class="meeting-items"
          >
            <router-link
              v-for="meeting in topMeetings"
              :key="meeting.id"
              :to="{ ...toPath, query: { meetingId: meeting.id }}"
              class="meeting-item"
            >
              <p class="meeting-title">
                {{ meeting.title || formatDate(meeting.meeting_date) }}
              </p>
              <p class="meeting-date">
                {{ relativeDate(meeting.meeting_date) }}
              </p>
            </router-link>
          </Stack>

          <div
            v-if="viewMore"
            style="margin-top: auto;"
          >
            <Button
              icon-before="fas fa-plus"
              :to="toPath"
              size="3_5"
              spacing="tight"
            >
              {{ viewMore }}
            </Button>
          </div>
        </Stack>

        <Callout
          v-else
          status="info"
          size="3_5"
        >
          No completed meetings
        </Callout>
      </Stack>
    </div>
  </Box>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Box, BoxHead, Button, Callout, DetailsList, DetailPair, LoaderWithText, Stack, Subtitle, Title } from '@stakeui';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import { relativeDate, formatDate, formatShortDate, prettifyMixedCase } from '@frontend/shared/Util.js';

const advisorStore = useAdvisorStore();

const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
});

const isFetching = ref(false);
const hasFetched = ref(false);
const completedMeetings = ref([]);
const upcomingMeetings = ref([]);

const fetchData = async () => {
  isFetching.value = true;

  try {
    const response = await fetch(`/api/v1/meetings/entity/${props.entityExternalId}/`, {
      method: 'GET',
      headers: advisorStore.headers,
    });


    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }

    const data = await response.json();
    const allMeetings = data.meetings || [];

    completedMeetings.value = allMeetings.filter(m => m.status === 'completed');
    upcomingMeetings.value = allMeetings.filter(m => m.status === 'upcoming');
  } catch (error) {
    console.error(`Error fetching`, error);
  } finally {
    isFetching.value = false;
    hasFetched.value = true;
  }
};

const toPath = { name: 'entity-note-taking' };

const link = computed(() => {
  return {
    to: toPath,
    label: 'View all',
  };
});

const MAX_COUNT = 7;
const topMeetings = computed(() => completedMeetings.value.slice(0, MAX_COUNT));
const viewMore = computed(() => completedMeetings.value.length > MAX_COUNT ? `View ${completedMeetings.value.length - MAX_COUNT} more` : '');

const nextMeeting = computed(() => {
  if (upcomingMeetings.value.length > 0) {
    return upcomingMeetings.value[0];
  }

  return null;
});

const upcomingMeeting = computed(() => {
  if (upcomingMeetings.value.length > 1) {
    return upcomingMeetings.value[1];
  }

  return null;
});

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.meetings-body {
  display: grid;
  grid-template-columns: 2fr 2fr;
  gap: 1em;
  flex-grow: 1;
  align-items: stretch;
}

.meetings-heading {
  color: var(--stk-text-weak);
}

.meeting-items {
  font-size: 0.875em;
  margin: 0 -0.5em;
}

.meeting-item {
  display: flex;
  color: var(--stk-text-strong);
  align-items: start;
  gap: 1em;
  line-height: 1.25;
  border-radius: 0.5em;
  padding: 0.25em 0.5em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, color;

  &:hover {
    background-color: var(--stk-primary-95);
    color: var(--stk-primary-30);
  }
}

.meeting-date {
  opacity: 0.5;
  flex-shrink: 0;
  width: 7em;
  text-align: right;
}

.meeting-title {
  color: inherit;
  flex-grow: 1;
}

.followups {
  flex-grow: 1;
}

.next-meeting,
.upcoming-meeting {
  background-color: #F9FAFB;
  border-radius: 0.75em;
  padding: 1.25em;
}

.next-meeting {
  flex-grow: 1;
}
</style>
